import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { QrCode, Package2 } from 'lucide-react';
import { PalletSummaryDto } from '../../../../types/picking';
import { parseGS1Barcode as parseGS1BarcodeFull } from '../../../../utils/gs1Parser';
import { ScanTextInput } from '../../../common/inputs';

// Extended interface to include batch and expiry data
export interface GS1ScanData {
  itemNo?: string;
  quantity?: number;
  sscc?: string;
  batchNo?: string;
  expiryDate?: string;
  gtin?: string;
  /**
   * Tempo-dom for skannefeltet (36334078): true når oppføringen ble TASTET
   * (tegn-gap > 150 ms, redigering underveis, liming/IME-innsetting, eller for
   * kort til å tempo-verifiseres). Wedge-skannere leverer tegn-for-tegn i burst
   * og klassifiseres som skann. Udefinert = kilden er tempo-verifisert andre
   * steder (global wedge-hook) eller ikke relevant.
   */
  typedManually?: boolean;
}

interface GS1ScannerProps {
  onScan: (data: GS1ScanData) => void;
  pallets: PalletSummaryDto[];
  activePallet: PalletSummaryDto | undefined;
  onSetActivePallet: (sscc: string) => void;
  onCreatePallet: (ssccCode?: string, gs1Data?: GS1ScanData) => void;
  onCompletePicking: () => void;
  onSendToPacking: () => void;
  /** When false, the "Til pakking" button is hidden (tenants not using the packing module). Default true. */
  showSendToPacking?: boolean;
  /** When true, scans are treated as location barcodes instead of item barcodes */
  isLocationScanOnlyMode?: boolean;
  /** Callback for location scan in location-scan-only mode */
  onLocationScan?: (barcode: string) => void;
  /**
   * Valgfri forhåndsbehandling av RÅ skannestreng (D-pak-flyten). Returnerer true når
   * skannet er konsumert — da hopper GS1Scanner over sin egen tolkning. Er proppen ikke
   * satt, eller returnerer den false, kjøres eksisterende logikk uendret.
   */
  onRawScan?: (raw: string) => Promise<boolean>;
}

export interface GS1ScannerRef {
  focusInput: () => void;
}

export const GS1Scanner = forwardRef<GS1ScannerRef, GS1ScannerProps>(({
  onScan,
  pallets,
  activePallet,
  onSetActivePallet,
  onCreatePallet,
  onCompletePicking,
  onSendToPacking,
  showSendToPacking = true,
  isLocationScanOnlyMode,
  onLocationScan,
  onRawScan
}, ref) => {
  const { t } = useTranslation();
  const [scanInput, setScanInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Tempo-måling for tastet-vs-skannet (36334078). Wedge-skannere leverer tegn
  // med ~10–30 ms mellomrom; mennesker klarer sjelden under 80 ms vedvarende.
  // Samme 150 ms-grense som useSimpleBarcodeScanner bruker globalt.
  const lastCharTimeRef = useRef<number>(0);
  const typedRef = useRef<boolean>(false);
  const trackedLengthRef = useRef<number>(0);

  const resetScanTiming = () => {
    typedRef.current = false;
    lastCharTimeRef.current = 0;
    trackedLengthRef.current = 0;
  };

  const handleInputChange = (value: string) => {
    const now = Date.now();
    const prevLen = trackedLengthRef.current;

    if (value.length === 0) {
      // Feltet tømt (bruker slettet alt) — ren tavle for neste oppføring
      resetScanTiming();
    } else if (value.length === prevLen + 1) {
      // Ett nytt tegn — mål gapet mot forrige
      if (lastCharTimeRef.current > 0 && now - lastCharTimeRef.current > 150) {
        typedRef.current = true;
      }
      lastCharTimeRef.current = now;
      trackedLengthRef.current = value.length;
    } else if (value.length < prevLen) {
      // Redigering/sletting underveis — skannere retter ikke
      typedRef.current = true;
      trackedLengthRef.current = value.length;
    } else {
      // Flere tegn i én endring (lim inn / IME-commit) — kan ikke tempo-verifiseres.
      // OBS: skanner-apper som injiserer hele strengen havner her → klassifiseres
      // som tastet. Verifiseres mot Haucons fysiske enheter (wedge = tegn-for-tegn).
      typedRef.current = true;
      lastCharTimeRef.current = now;
      trackedLengthRef.current = value.length;
    }
    setScanInput(value);
  };

  // Expose focus function to parent component
  useImperativeHandle(ref, () => ({
    focusInput: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  // Auto-focus disabled - global scanning is active
  // useEffect(() => {
  //   if (inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, []);

  // Use the comprehensive GS1 parser from utils
  const parseAndExtractGS1Data = (barcode: string): GS1ScanData => {
    // Use the robust parser from gs1Parser.ts
    const fullGS1Data = parseGS1BarcodeFull(barcode);

    console.log('📦 Full GS1 parsed data:', fullGS1Data);

    // Extract relevant fields for picking
    return {
      sscc: fullGS1Data.sscc,
      gtin: fullGS1Data.gtin,
      itemNo: fullGS1Data.gtin, // Use GTIN as itemNo (matching handles cleaning)
      quantity: fullGS1Data.quantity,
      batchNo: fullGS1Data.batchNo,
      expiryDate: fullGS1Data.expiryDate || fullGS1Data.bestBeforeDate || fullGS1Data.useByDate
    };
  };

  const handleScanItem = async () => {
    if (!scanInput) return;

    // Prevent double-scanning - ignore new scans while processing
    if (isProcessing) {
      console.log('⏸️ GS1Scanner: Already processing, ignoring scan');
      return;
    }

    setIsProcessing(true);
    console.log('🔍 GS1Scanner: Starting scan with input:', scanInput);

    // Tempo-dom for DENNE oppføringen (36334078): avgjøres nå, før feltet tømmes.
    // Under 3 tegn kan ikke tempo-verifiseres → regnes som tastet.
    const typedManually = typedRef.current || scanInput.length < 3;

    // In location-scan-only mode, route directly to location handler
    if (isLocationScanOnlyMode && onLocationScan) {
      const cleanedBarcode = scanInput.replace(/[~§]+$/g, '').trim();
      console.log('📍 GS1Scanner: Location-scan-only mode, routing to location handler:', cleanedBarcode);
      onLocationScan(cleanedBarcode);
      setScanInput('');
      resetScanTiming();
      setTimeout(() => setIsProcessing(false), 100);
      return;
    }

    // D-pak-skann prøves før GS1-tolkningen under. Håndteres skannet der, er vi ferdige.
    if (onRawScan && await onRawScan(scanInput)) {
      setScanInput('');
      resetScanTiming();
      setTimeout(() => setIsProcessing(false), 100);
      return;
    }

    // First, remove trailing scanner separators to check for real GS1 structure
    const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

    // Check if this looks like a GS1-128 structured barcode
    // GS1-128 has separators WITHIN the string (not just trailing), or ]C1 prefix
    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                   withoutTrailingSeparators.includes('§') ||
                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
    const hasParentheses = scanInput.includes('(');
    const hasC1Prefix = scanInput.startsWith(']C1');

    // A real GS1 barcode has structure indicators, not just trailing separators from scanner
    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

    // Try GS1 parsing if it has GS1 structure indicators
    let fullGS1Data = null;
    let hasGS1Data = false;

    if (looksLikeGS1) {
      console.log('🔍 GS1 structure detected, attempting GS1 parse...');
      fullGS1Data = parseGS1BarcodeFull(scanInput);
      hasGS1Data = !!(fullGS1Data.parsedAIs && Object.keys(fullGS1Data.parsedAIs).length > 0);
      console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', fullGS1Data?.parsedAIs);
    } else {
      console.log('📝 No GS1 structure detected, treating as plain barcode');
    }

    if (hasGS1Data) {
      console.log('📊 GS1Scanner: Detected GS1-128 barcode');
      const gs1Data = parseAndExtractGS1Data(scanInput);
      console.log('📋 GS1Scanner: Extracted data:', gs1Data);

        // Determine what type of scan this is
        const hasSSCC = !!gs1Data.sscc;
        const hasItem = !!(gs1Data.itemNo || gs1Data.gtin);

        // If no relevant GS1 data (no SSCC and no Item), treat as plain barcode
        if (!hasSSCC && !hasItem) {
          console.log('⚠️ GS1 detected but no SSCC/GTIN found - treating as plain barcode');
          // Strip trailing scanner separators from plain barcode
          const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
          onScan({ itemNo: cleanedItemNo, typedManually });
        }
        // PRIORITY 1: If both SSCC and Item are present, this is a pre-filled pallet scan
        else if (hasSSCC && hasItem) {
          console.log('📦 Pre-filled pallet scan detected (SSCC + Item)');
          // Send to parent - let parent handle both pallet creation AND item registration
          onScan({ ...gs1Data, typedManually });
        }
        // PRIORITY 2: SSCC only - pallet activation/creation
        else if (hasSSCC && !hasItem) {
          console.log('📦 SSCC-only scan - pallet management');
          let targetPallet = pallets.find(p => p.sscc === gs1Data.sscc);

          if (!targetPallet) {
            console.log('➕ Creating empty pallet with SSCC:', gs1Data.sscc);
            onCreatePallet(gs1Data.sscc);
          } else {
            console.log('✅ Activating existing pallet:', targetPallet.sscc);
            onSetActivePallet(targetPallet.sscc);
          }
        }
        // PRIORITY 3: Item only (no SSCC) - item validation/scanning
        else if (!hasSSCC && hasItem) {
          console.log('🔎 Item-only scan (validation/picking)');
          onScan({ ...gs1Data, typedManually });
        }

      setScanInput('');
      resetScanTiming();

      // Allow next scan after a short delay to ensure UI has updated
      setTimeout(() => setIsProcessing(false), 100);
      return;
    }

    // Not GS1-128 or parsing failed - treat as plain barcode
    console.log('📝 GS1Scanner: Plain barcode (not GS1-128)');
    // Strip trailing scanner separators (~ and §) from plain barcodes
    const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
    console.log('🧹 Cleaned plain barcode:', cleanedItemNo);
    // Plain item scan - just item number (matching logic handles GTIN cleaning)
    onScan({ itemNo: cleanedItemNo, typedManually });

    setScanInput('');
    resetScanTiming();

    // Allow next scan after a short delay to ensure UI has updated
    setTimeout(() => setIsProcessing(false), 100);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
      {/* Action Buttons */}
      <div className={`grid ${showSendToPacking ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
        <button
          onClick={onCompletePicking}
          className="px-4 py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
        >
          {t('warehouse.modules.picking.actions.completePick')}
        </button>
        {showSendToPacking && (
          <button
            onClick={onSendToPacking}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {t('warehouse.modules.picking.actions.sendToPacking')}
          </button>
        )}
      </div>

      {/* Scanner Input */}
      <div className="flex gap-2">
        <ScanTextInput
          ref={inputRef}
          placeholder={isLocationScanOnlyMode ? t('warehouse.modules.picking.gs1.scanLocationPlaceholder') : t('warehouse.modules.picking.placeholders.scanBarcode')}
          className="flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          value={scanInput}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleScanItem()}
          disabled={isProcessing}
        />
        <button
          onClick={handleScanItem}
          disabled={isProcessing}
          className={`p-2.5 rounded-lg ${
            isProcessing
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white`}
          title={t('common.buttons.scan')}
        >
          <QrCode size={20} />
        </button>
      </div>
    </div>
  );
});
